import React, { useCallback, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { observer } from "mobx-react-lite"
import { IconPencil } from "@tabler/icons-react"
import InfiniteScroll from "react-infinite-scroll-component"
import { Link } from "react-router-dom"
import {
  Table,
  Button,
  Loader,
  Tooltip,
  Group,
  Text,
  ActionIcon,
  Title,
  Badge,
  Stack,
  Flex,
  ScrollArea,
  Center,
} from "@mantine/core"
import { PATHS } from "../../shared/lib/paths"
import { courseStore } from "../../app/store"
import { ErrorResult } from "../../shared/ui/ErrorResult"
import { EmptyResult } from "../../shared/ui/EmptyResult"
import { type CourseDto, getEventStatus } from "../../common"
import { CourseService } from "../../app/services"
import {
  CourseDeleteModal,
  CourseSearch,
  CoursePublishedCheckbox,
} from "../../features/courses"
import { fDate } from "../../shared/lib/dates"
import { Image } from "../../shared/ui/Image"
import { API_URL } from "../../shared/config"

export function CourseRow(course: CourseDto): React.JSX.Element {
  const {
    name,
    slug,
    _id,
    published,
    cover,
    landing: { startDate },
  } = course

  return (
    <tr>
      <td>
        <Flex align="center" gap={24}>
          <Image
            ratio="1/1"
            w={120}
            objectFit="contain"
            sx={{ borderRadius: "50%" }}
            src={`${API_URL}/public${cover}`}
          />
          <Stack spacing={12}>
            <Stack spacing={0}>
              <Text color="blue" component={Link} fz="xl" fw={600} to={PATHS.TRAINING.COURSES.DETAILS(_id, slug)}>
                {name}
              </Text>
              <Text c="dimmed">{slug}</Text>
            </Stack>

            <Stack spacing={2}>
              <Text>{`Добавлен: ${fDate(course.createdAt, "D MMMM YYYY")}`}</Text>
              <Text>{`Обновлен: ${fDate(course.updatedAt, "D MMMM YYYY")}`}</Text>
            </Stack>
          </Stack>
        </Flex>
      </td>
      <td>
        <Stack spacing={0}>
          <Text fz="lg" fw={600}>
            {fDate(startDate, "D MMMM YYYY")}
          </Text>
          <Text c="dimmed">
            {getEventStatus(startDate)}
          </Text>
        </Stack>
      </td>
      <td>
        <Center>
          <Badge color={published ? "green" : "red"}>
            {published ? "Да" : "Нет"}
          </Badge>
        </Center>
      </td>
      <td>
        <Group position="right" noWrap>
          <Tooltip label="Редактировать">
            <ActionIcon radius="md" size="xl" component={Link} to={PATHS.DASHBOARD.TRAINING.COURSES.EDIT(_id)}>
              <IconPencil stroke={1.5}/>
            </ActionIcon>
          </Tooltip>
          <CourseDeleteModal {...{ name, _id }} />
        </Group>
      </td>
    </tr>
  )
}

export const CoursesTablePage = observer(function CoursesTablePage() {
  const { courses, isLoading, totalCount, error } = courseStore

  useEffect(() => {
    void CourseService.load()
  }, [])

  const next = useCallback(() => {
    void CourseService.loadMore()
  }, [])

  const renderInfo = () => {
    if (isLoading) {
      return (
        <Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
          <Loader size="lg" variant="dots"/>
          <Text size="lg" mt="xs">
            Загружаем данные...
          </Text>
        </Flex>
      )
    }

    if (error) {
      return <ErrorResult errorMessage={error}/>
    }

    if (!courses.length) {
      return <EmptyResult/>
    }

    return (
      <Group position="center">
        <Badge color="gray" size="lg" mt="xl">
          Вы загрузили все учебные курсы
        </Badge>
      </Group>
    )
  }

  return (
    <>
      <Helmet>
        <title>Учебные курсы - админ. панель</title>
      </Helmet>

      <Flex direction="column" w="100%">
        <Group position="apart" spacing="xl" mb="xl" pb="md">
          <Title order={1} size={24}>
            Учебные курсы
          </Title>
          <Button
            radius="md"
            component={Link}
            to={PATHS.DASHBOARD.TRAINING.COURSES.CREATE}
          >
            Добавить курс
          </Button>
        </Group>

        <Group position="apart" mb="sm" spacing="xl">
          <CourseSearch size="md" radius="md" sx={{ flex: "auto" }}/>
          <CoursePublishedCheckbox/>
        </Group>

        <ScrollArea sx={{ flex: "auto" }}>
          <InfiniteScroll
            next={next}
            dataLength={courses.length}
            hasMore={totalCount !== courses.length}
            loader={
              <Flex mt="md" direction="column" align="center" justify="center" sx={{ flex: 1 }}>
                <Loader size="lg" variant="dots"/>
                <Text size="lg" mt="xs">
                  Загружаем данные...
                </Text>
              </Flex>
            }
            endMessage={renderInfo()}
            style={{ overflow: "unset" }}
          >
            <Table
              highlightOnHover
              sx={{ minWidth: 800 }}
              verticalSpacing="sm"
            >
              <thead>
              <tr>
                <th>Название</th>
                <th>Дата начала</th>
                <th style={{ textAlign: "center" }}>Опубликован</th>
                <th style={{ textAlign: "right" }}>Действия</th>
              </tr>
              </thead>
              <tbody>
              {courses.map((course) => (
                <CourseRow key={course._id} {...course} />
              ))}
              </tbody>
            </Table>
          </InfiniteScroll>
        </ScrollArea>

      </Flex>
    </>
  )
})
